import { useContext, useEffect, useState } from "react";
import { Nav, NavItem, NavLink, TabContent, TabPane, Button } from "reactstrap";
import {
  HerdScenarioRange,
  HerdScenario,
  DefaultHerdScenario,
} from "../models/cow-model-parameters.model";
import {
  FlockScenarioRange,
  FlockScenario,
  DefaultFlockScenario,
} from "../models/sheep-model-parameters.model";
import { HerdParameterForm } from "../components/cows/HerdParameterForm";
import { ValueForecast } from "../components/ValueForecast";
import { SelectLivestockForm } from "../components/SelectLivestockForm";
import { FlockParameterForm } from "../components/sheep/FlockParameterForm";
import { getGroupValidation, Group } from "../models/group.model";
import { getHerdValue } from "../models/cow-herd-value.model";
import { getFlockValue } from "../models/sheep-flock-value.model";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faRefresh, faShare } from "@fortawesome/free-solid-svg-icons";
import { ShareModal } from "../components/ShareModal";
import { AppContext } from "../AppContext";
import { throwExpression } from "../util";
import { useNavigate } from "react-router-dom";
import { RoutePaths } from "../constants";
import { ResetForecastModal } from "../components/ResetForecastModal";
import { AboutModal } from "../components/AboutModal";
import { HeaderBrand } from "./header/Brand";
import { StartForecastPrompt } from "./StartForecastPrompt";
import { RoundedCard } from "./RoundedCard";
import { trackButtonClick } from "../application-insights-config";

export const TabLayout = () => {
  const { model, setModel, activeTab, setActiveTab, reset } =
    useContext(AppContext) ?? throwExpression("No AppContext");

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const toggleShareModal = () => setShareModalOpen(!shareModalOpen);

  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const toggleAboutModal = () => setAboutModalOpen(!aboutModalOpen);

  const [newForecastModalOpen, setNewForecastModalOpen] = useState(false);
  const toggleNewForecastModal = () =>
    setNewForecastModalOpen(!toggleNewForecastModal);

  const navigate = useNavigate();

  useEffect(() => {
    if (activeTab !== 4) setShareModalOpen(false);
  }, [activeTab]);

  const isCows = model.kind === "cows";
  const isSheep = model.kind === "sheep";

  const setKind = (kind: "cows" | "sheep") => {
    if (model.kind === kind) return;
    setModel((m) => ({
      ...m,
      kind,
      scenario: kind === "cows" ? DefaultHerdScenario : DefaultFlockScenario,
    }));
  };

  const setGroup = (group: Group) => setModel((m) => ({ ...m, group }));

  const setTargetScenario = (scenario: any) => {
    setModel((m) => ({ ...m, scenario }));
  };

  const setForecastName = (forecastName: string) =>
    setModel((m) => ({ ...m, forecastName }));

  const isValidGroup =
    model.group !== undefined && getGroupValidation(model.group)[0];
  const isHerdDefined = isCows && isValidGroup;
  const isFlockDefined = isSheep && isValidGroup;

  const isHerdOrFlockDefined = isHerdDefined || isFlockDefined;

  let ranges;
  if (isHerdDefined) {
    ranges = HerdScenarioRange;
    const herdValue = getHerdValue(
      model.group as Required<Group>,
      model.scenario as HerdScenario
    );
    Object.values(ranges).forEach((range) => {
      if (range.getFormatter) range.format = range.getFormatter(herdValue);
      if (range.getForwardTransform)
        range.forwardTransform = range.getForwardTransform(herdValue);
      if (range.getReverseTransform)
        range.reverseTransform = range.getReverseTransform(herdValue);
    });
  } else if (isFlockDefined) {
    ranges = FlockScenarioRange;
    const flockValue = getFlockValue(
      model.group as Required<Group>,
      model.scenario as FlockScenario
    );
    Object.values(ranges).forEach((range) => {
      if (range.getFormatter) range.format = range.getFormatter(flockValue);
      if (range.getForwardTransform)
        range.forwardTransform = range.getForwardTransform(flockValue);
      if (range.getReverseTransform)
        range.reverseTransform = range.getReverseTransform(flockValue);
    });
  }

  return (
    <>
      <header>
        <Nav tabs className="position-relative header-bar">
          <NavItem>
            <NavLink
              style={{ paddingLeft: 6, paddingRight: 6 }}
              onClick={() => navigate(RoutePaths.landing)}
            >
              <FontAwesomeIcon icon={faHome} size={"lg"} />
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={activeTab === 0 ? "active" : ""}
              onClick={() => setActiveTab(0)}
            >
              Livestock
            </NavLink>
          </NavItem>
          {/* TODO: consider removing this unused target adjustment tab */}
          {/* <NavItem>
          <NavLink
            className={activeTab === 2 ? "active" : ""}
            onClick={() => setActiveTab(2)}
            disabled={!isHerdOrFlockDefined}
          >
            Target
          </NavLink>
        </NavItem> */}
          {/* TODO: consider removing this unused range adjustment tab */}
          {/* <NavItem>
          <NavLink
            className={activeTab === 3 ? "active" : ""}
            onClick={() => setActiveTab(3)}
            disabled={herdParams === undefined}
          >
            Adjust Range
          </NavLink>
        </NavItem> */}
          <NavItem>
            <NavLink
              className={activeTab === 4 ? "active" : ""}
              onClick={() => setActiveTab(4)}
              disabled={!isHerdOrFlockDefined}
            >
              Forecast
            </NavLink>
          </NavItem>
          <HeaderBrand />
          <NavItem style={{ marginLeft: "auto" }}>
            <NavLink onClick={toggleAboutModal}>About</NavLink>
          </NavItem>
        </Nav>
      </header>
      <main>
        <TabContent activeTab={activeTab}>
          <TabPane tabId={0}>
            <RoundedCard className="mt-2" id="select-livestock">
              <SelectLivestockForm
                livestock={model.kind}
                setLivestock={setKind}
              />
              <StartForecastPrompt isCows={isCows} isSheep={isSheep} />
              {isCows && (
                <HerdParameterForm
                  submit={() => {
                    trackButtonClick("start_new_forecast", "cattle");
                    setActiveTab(4);
                  }}
                  herd={model.group ?? {}}
                  setHerd={setGroup}
                />
              )}
              {isSheep && (
                <FlockParameterForm
                  submit={() => {
                    trackButtonClick("start_new_forecast", "sheep");
                    setActiveTab(4);
                  }}
                  flock={model.group ?? {}}
                  setFlock={setGroup}
                />
              )}
            </RoundedCard>
          </TabPane>
          {/* TODO: consider removing this unused TabPane */}
          {/* <TabPane tabId={2}>
          {activeTab === 2 && isValidGroup && ranges && (
            <TargetFormTabbed
              classifications={
                model.kind === "cows"
                  ? CowClassifications
                  : SheepClassifications
              }
              submit={() => setActiveTab(4)}
              target={model.scenario as HerdScenario | FlockScenario}
              setTarget={setTargetScenario}
              ranges={ranges}
              markerValues={
                model.kind === "cows"
                  ? {
                      best: BestHerdScenario,
                      worst: WorstHerdScenario,
                      // TODO: re-instate reference markers if we have the data?
                      //reference: ReferenceHerdScenario,
                    }
                  : {
                      best: BestFlockScenario,
                      worst: WorstFlockScenario,
                      // TODO: re-instate reference markers if we have the data?
                      //reference: ReferenceFlockScenario,
                    }
              }
            />
          )}
        </TabPane> */}
          {/* TODO: consider removing this unused TabPane */}
          {/* <TabPane tabId={3}>
          <Row>
            <Col sm="12">
              <HerdRangeForm submit={() => setActiveTab(4)} />
            </Col>
          </Row>
        </TabPane> */}
          <TabPane tabId={4}>
            <ResetForecastModal
              isOpen={newForecastModalOpen}
              toggle={toggleNewForecastModal}
              onConfirm={reset}
            />
            <ShareModal
              model={model}
              isOpen={shareModalOpen}
              toggle={toggleShareModal}
              setForecastName={setForecastName}
            />
            <div
              className="w-100 d-flex align-items-center justify-content-between px-3 my-2"
              id="value-forecast-header"
            >
              <div className="flex-grow-1" />
              <div className="d-flex gap-2 my-2">
                <Button
                  color="dark"
                  outline
                  onClick={() => setNewForecastModalOpen(true)}
                >
                  <FontAwesomeIcon
                    icon={faRefresh}
                    className="me-2"
                    size="sm"
                  />
                  <span>Reset</span>
                </Button>
                <Button color="dark" onClick={() => setShareModalOpen(true)}>
                  <FontAwesomeIcon icon={faShare} className="me-2" size="sm" />
                  <span>Share</span>
                </Button>
              </div>
            </div>
            {model.scenario !== undefined && (
              <ValueForecast model={model} setModel={setModel} />
            )}
          </TabPane>
        </TabContent>
      </main>
      <AboutModal isOpen={aboutModalOpen} toggle={toggleAboutModal} />
    </>
  );
};
